import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocaleDateTime } from "@/utils/formatUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteContractIncrement, useFilterContractIncrements } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { useHasPermissions } from "../../../auth/hooks";
import { ContractType } from "../../enums";
import { ContractIncrement } from "../../types";
import { contractPermissionsByContractType } from "../../utils";

type Props = {
  contractId: string;
  contractType: ContractType;
  onUpdate: (contractIncrement: ContractIncrement) => void;
};

export const ContractIncrementsTable = ({ contractId, contractType, onUpdate }: Props) => {
  const { t } = useTranslation();

  const [pageIndex, setPageIndex] = useState(0);

  const hasPrivilegedChangesOnVerifiedPermission = useHasPermissions(
    contractPermissionsByContractType[contractType].privilegedChangesOnVerified,
  );

  const { data, isFetching } = useFilterContractIncrements({
    id: contractId,
    object: { pageIndex, pageSize: PageSizes.LARGE },
  });
  const deleteContractIncrement = useDeleteContractIncrement();

  const columns: ColumnsType<ContractIncrement> = useMemo(() => {
    const columnsData: ColumnsType<ContractIncrement> = [
      {
        key: "createdAt",
        title: t("common.createdAt"),
        width: 160,
        render: (_, record) => formatLocaleDateTime(record.createdAt),
      },
      {
        key: "updatedAt",
        title: t("common.updatedAt"),
        width: 160,
        render: (_, record) => formatLocaleDateTime(record.updatedAt),
      },
      {
        key: "signDate",
        title: t("contract.attrs.incrementSignDate"),
        width: 130,
        render: (_, record) => formatLocaleDate(record.signDate),
      },
      {
        key: "baseAmount",
        width: 200,
        align: "right",
        title:
          contractType === ContractType.INSURANCE_CONTRACT
            ? t("contract.attrs.annualPremium")
            : t("contract.attrs.prepaidTargetAmount"),
        render: (_, record) => (
          <>
            <Tooltip title={t("contract.helpers.previousValue")}>
              <del className="sub-header-info">
                {isDefined(record.previousBaseAmount) ? formatLocaleCurrency(record.previousBaseAmount) : "-"}
              </del>
            </Tooltip>
            {` → `}
            {isDefined(record.newBaseAmount) ? formatLocaleCurrency(record.newBaseAmount) : "-"}
          </>
        ),
      },
      {
        key: "installmentAmount",
        width: 200,
        align: "right",
        title:
          contractType === ContractType.INSURANCE_CONTRACT
            ? t("contract.attrs.partialPremium")
            : t("contract.attrs.monthlyPayment"),
        render: (_, record) => (
          <>
            <Tooltip title={t("contract.helpers.previousValue")}>
              <del className="sub-header-info">{formatLocaleCurrency(record.previousInstallmentAmount)}</del>
            </Tooltip>
            {` → `}
            {formatLocaleCurrency(record.newInstallmentAmount)}
          </>
        ),
      },
      {
        key: "createdBy",
        width: 120,
        title: t("contract.helpers.createdBy"),
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.createdBy?.name}</Ellipsis>,
      },
    ];

    if (hasPrivilegedChangesOnVerifiedPermission)
      columnsData.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdate(record)} />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("contract.helpers.contractIncrementDeleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => deleteContractIncrement.mutate({ id1: contractId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });

    return columnsData;
  }, [contractType, hasPrivilegedChangesOnVerifiedPermission, t]);

  return (
    <Table<ContractIncrement>
      {...tableStandardProps()}
      columns={columns}
      loading={isFetching}
      scroll={{ x: TableSizes.LARGE }}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => setPageIndex(pageIndex - 1),
      }}
    />
  );
};
